export interface StationTrack {
  id: string;
  title: string;
  audioUrl: string;
  durationSeconds: number;
  speaker?: string;
  imageUrl?: string;
}

export interface StationPosition {
  index: number;
  offsetSeconds: number;
}

export interface NowPlayingSchedule {
  available: boolean;
  serverTime: number;
  /** Fixed station epoch (ms) the continuous loop is anchored to. */
  anchorMs: number;
  totalDuration: number;
  currentIndex: number;
  offsetSeconds: number;
  playlist: StationTrack[];
}

/** Recompute the live position for a given corrected server-time (ms). */
export function resolveAt(schedule: NowPlayingSchedule, serverNowMs: number): StationPosition {
  if (!schedule.available || schedule.totalDuration <= 0 || schedule.playlist.length === 0) {
    return { index: 0, offsetSeconds: 0 };
  }

  const elapsed = (serverNowMs - schedule.anchorMs) / 1000;
  const clamped = elapsed < 0 ? 0 : elapsed;
  const posInCycle = clamped % schedule.totalDuration;

  let acc = 0;
  for (let i = 0; i < schedule.playlist.length; i++) {
    const d = schedule.playlist[i]!.durationSeconds;
    if (posInCycle < acc + d) {
      return { index: i, offsetSeconds: posInCycle - acc };
    }
    acc += d;
  }

  return { index: schedule.playlist.length - 1, offsetSeconds: 0 };
}

/** Clock skew against the server, and the programmed position that follows from it. */
export class StationSyncController {
  private clockSkewMs = 0;

  updateClockSkew(serverTimeMs: number, deviceNowMs: number = Date.now()): void {
    this.clockSkewMs = serverTimeMs - deviceNowMs;
  }

  get serverNowMs(): number {
    return Date.now() + this.clockSkewMs;
  }

  resolvePosition(schedule: NowPlayingSchedule): StationPosition {
    return resolveAt(schedule, this.serverNowMs);
  }
}

/**
 * Drives the idle "now airing" display by waking exactly at each track
 * boundary — not on an interval — so the pre-play card stays current with
 * near-zero battery cost (one timer per track, device sleeps in between, and
 * no network: position is pure clock math on the already-fetched schedule).
 *
 * Resyncs on `visibilitychange` because mobile browsers suspend/throttle
 * timers in the background; on resume `tick()` recomputes from the clock and
 * self-corrects regardless of how late the timer fired.
 *
 * Lifecycle: `start(schedule)` while idle, `stop()` when playback engages or
 * on teardown. The callback fires only when the airing index actually changes.
 */
export class StationIdleClock {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private schedule: NowPlayingSchedule | null = null;
  private lastIndex = -1;
  private listening = false;

  private readonly onVisibility = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      this.tick();
    }
  };

  constructor(
    private readonly sync: StationSyncController,
    private readonly onIndex: (index: number) => void,
  ) {}

  /** Begin (or restart) tracking. Emits the current index synchronously. */
  start(schedule: NowPlayingSchedule): void {
    this.schedule = schedule;
    this.lastIndex = -1;
    this.addVisibilityListener();
    this.tick();
  }

  /** Stop tracking and release the timer + listener. */
  stop(): void {
    this.clearTimer();
    this.removeVisibilityListener();
    this.schedule = null;
    this.lastIndex = -1;
  }

  private tick(): void {
    this.clearTimer();
    const schedule = this.schedule;
    if (!schedule?.available || schedule.playlist.length === 0) return;

    const pos = this.sync.resolvePosition(schedule);
    if (pos.index !== this.lastIndex) {
      this.lastIndex = pos.index;
      this.onIndex(pos.index);
    }

    const track = schedule.playlist[pos.index];
    if (!track) return;
    // Wake just past the boundary (+50ms) so we never fire a hair early and
    // re-resolve the same track. Math.max guards against drift past the end.
    const msToBoundary = Math.max(0, (track.durationSeconds - pos.offsetSeconds) * 1000);
    this.timer = setTimeout(() => this.tick(), msToBoundary + 50);
  }

  private clearTimer(): void {
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private addVisibilityListener(): void {
    if (this.listening || typeof document === "undefined") return;
    document.addEventListener("visibilitychange", this.onVisibility);
    this.listening = true;
  }

  private removeVisibilityListener(): void {
    if (!this.listening || typeof document === "undefined") return;
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.listening = false;
  }
}
